import React, { useState, useMemo, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Archive,
  AlertCircle,
  History,
} from "lucide-react";
import { toast } from "sonner";
import { ERGOS_MODULES, getModuleByKey } from "@/../../shared/ergos-modules";

interface SuggestionItem {
  id: number;
  versionId: number;
  title: string | null;
  recommendationText: string;
  recommendationType: "PREVENTION" | "MITIGATION_ELIMINATION";
  moduleKey?: string | null; // Chave do módulo ERGOS (vem do trigger ERGOS_MODULE)
  moduleLabel?: string | null; // Label do módulo ERGOS (vem do trigger ERGOS_MODULE)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: number;
  updatedBy: number;
}

interface SuggestionVersion {
  id: number;
  name: string;
  notes: string | null;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  createdAt: Date;
  updatedAt: Date;
  createdBy: number;
  updatedBy: number;
  publishedAt: Date | null;
  publishedBy: number | null;
  archivedAt: Date | null;
  archivedBy: number | null;
}

export function SuggestionsMatrixTab() {
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState<"ALL" | "PREVENTION" | "MITIGATION_ELIMINATION">("ALL");
  const [filterStatus, setFilterStatus] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");
  const [editingItem, setEditingItem] = useState<Partial<SuggestionItem> | null>(null);
  const [editingModuleKey, setEditingModuleKey] = useState<string>("");
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<number | null>(null);

  // Queries
  const versionsQuery = trpc.suggestions.getVersions.useQuery(undefined, {
    staleTime: 60000,
    gcTime: 600000,
  });

  const itemsQuery = trpc.suggestions.getItemsByVersion.useQuery(
    { versionId: selectedVersionId || 0 },
    { enabled: !!selectedVersionId, staleTime: 60000, gcTime: 600000 }
  );

  const auditLogQuery = trpc.suggestions.getAuditLog.useQuery(
    { versionId: selectedVersionId || 0 },
    { enabled: !!selectedVersionId && isAuditModalOpen, staleTime: 60000 }
  );

  // Mutations
  const createVersionMutation = trpc.suggestions.createVersion.useMutation({
    onSuccess: () => {
      toast.success("Versão DRAFT criada com sucesso");
      versionsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao criar versão: " + error.message);
    },
  });

  const publishVersionMutation = trpc.suggestions.publishVersion.useMutation({
    onSuccess: () => {
      toast.success("Versão publicada com sucesso");
      setShowPublishConfirm(false);
      versionsQuery.refetch();
      itemsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao publicar versão: " + error.message);
    },
  });

  const archiveVersionMutation = trpc.suggestions.archiveVersion.useMutation({
    onSuccess: () => {
      toast.success("Versão arquivada com sucesso");
      setShowArchiveConfirm(false);
      versionsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao arquivar versão: " + error.message);
    },
  });

  const cloneVersionMutation = trpc.suggestions.cloneVersion.useMutation({
    onSuccess: () => {
      toast.success("Versão clonada com sucesso");
      setShowCloneDialog(false);
      setCloneName("");
      versionsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao clonar versão: " + error.message);
    },
  });

  const createItemMutation = trpc.suggestions.createItem.useMutation({
    onSuccess: () => {
      toast.success("Item criado com sucesso");
      setIsEditDrawerOpen(false);
      setEditingItem(null);
      itemsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao criar item: " + error.message);
    },
  });

  const updateItemMutation = trpc.suggestions.updateItem.useMutation({
    onSuccess: () => {
      toast.success("Item atualizado com sucesso");
      setIsEditDrawerOpen(false);
      setEditingItem(null);
      itemsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao atualizar item: " + error.message);
    },
  });

  const deleteItemMutation = trpc.suggestions.deleteItem.useMutation({
    onSuccess: () => {
      toast.success("Item deletado com sucesso");
      itemsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao deletar item: " + error.message);
    },
  });

  const toggleItemMutation = trpc.suggestions.toggleItemActive.useMutation({
    onSuccess: () => {
      itemsQuery.refetch();
    },
    onError: (error) => {
      toast.error("Erro ao alternar status: " + error.message);
    },
  });

  // Set initial version
  React.useEffect(() => {
    if (versionsQuery.data && versionsQuery.data.length > 0 && !selectedVersionId) {
      const published = versionsQuery.data.find((v) => v.status === "PUBLISHED");
      const draft = versionsQuery.data.find((v) => v.status === "DRAFT");
      setSelectedVersionId(published?.id || draft?.id || versionsQuery.data[0].id);
    }
  }, [versionsQuery.data, selectedVersionId]);

  // Filter items
  const filteredItems = useMemo(() => {
    if (!itemsQuery.data) return [];

    return itemsQuery.data.filter((item) => {
      const matchesSearch =
        !searchText ||
        item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.recommendationText.toLowerCase().includes(searchText.toLowerCase());

      const matchesType =
        filterType === "ALL" || item.recommendationType === filterType;

      const matchesStatus =
        filterStatus === "ALL" ||
        (filterStatus === "ACTIVE" && item.isActive) ||
        (filterStatus === "INACTIVE" && !item.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [itemsQuery.data, searchText, filterType, filterStatus]);

  const selectedVersion = versionsQuery.data?.find(
    (v) => v.id === selectedVersionId
  );

  const isPublished = selectedVersion?.status === "PUBLISHED";
  const isArchived = selectedVersion?.status === "ARCHIVED";
  const isDraft = selectedVersion?.status === "DRAFT";

  const handleCreateVersion = useCallback(() => {
    createVersionMutation.mutate({ name: `Biblioteca v${Date.now()}` });
  }, [createVersionMutation]);

  const handlePublish = useCallback(() => {
    if (!selectedVersionId) return;
    publishVersionMutation.mutate({ versionId: selectedVersionId });
  }, [selectedVersionId, publishVersionMutation]);

  const handleArchive = useCallback(() => {
    if (!selectedVersionId) return;
    archiveVersionMutation.mutate({ versionId: selectedVersionId });
  }, [selectedVersionId, archiveVersionMutation]);

  const handleClone = useCallback(() => {
    if (!selectedVersionId || !cloneName.trim()) return;
    cloneVersionMutation.mutate({
      sourceVersionId: selectedVersionId,
      newName: cloneName.trim(),
    });
  }, [selectedVersionId, cloneName, cloneVersionMutation]);

  const handleCreateItem = useCallback(() => {
    if (!selectedVersionId) return;
    setEditingItem({
      versionId: selectedVersionId,
      title: "",
      recommendationText: "",
      recommendationType: "PREVENTION",
      moduleKey: "", // Obrigatório no CREATE
      isActive: true,
    });
    setEditingModuleKey(""); // Resetar seleção de módulo
    setIsEditDrawerOpen(true);
  }, [selectedVersionId]);

  const handleEditItem = useCallback((item: SuggestionItem) => {
    setEditingItem(item);
    setEditingModuleKey(item.moduleKey || ""); // Carregar módulo existente
    setIsEditDrawerOpen(true);
  }, []);

  const handleSaveItem = useCallback(() => {
    if (!editingItem) return;

    // Validação: Módulo obrigatório no CREATE
    if (!editingItem.id && !editingModuleKey) {
      toast.error("Selecione um módulo ERGOS.");
      return;
    }

    // Validação: Descrição obrigatória
    if (!editingItem.recommendationText?.trim()) {
      toast.error("O texto da recomendação é obrigatório");
      return;
    }

    // Título auto-gerado
    const autoGeneratedTitle =
      editingModuleKey && editingItem.recommendationType
        ? `${getModuleByKey(editingModuleKey)?.label || "Módulo"} - ${
            editingItem.recommendationType === "PREVENTION"
              ? "Prevenção"
              : "Mitigação/Eliminação"
          }`
        : null;

    if (editingItem.id) {
      // EDIT: NÃO envia moduleKey (backend bloqueia alteração)
      updateItemMutation.mutate({
        id: editingItem.id,
        title: autoGeneratedTitle,
        recommendationText: editingItem.recommendationText,
        recommendationType: editingItem.recommendationType as any,
        isActive: editingItem.isActive, // ✅ CORREÇÃO: Enviar isActive
      });
    } else {
      // CREATE: Envia moduleKey para criar trigger ERGOS_MODULE
      createItemMutation.mutate({
        versionId: editingItem.versionId!,
        title: autoGeneratedTitle,
        recommendationText: editingItem.recommendationText,
        recommendationType: editingItem.recommendationType as any,
        moduleKey: editingModuleKey, // Novo campo
        isActive: true,
      });
    }
  }, [editingItem, editingModuleKey, createItemMutation, updateItemMutation, toast]);

  const handleDeleteItem = useCallback(
    (itemId: number) => {
      setDeleteConfirmItem(itemId);
    },
    []
  );

  const confirmDeleteItem = useCallback(() => {
    if (deleteConfirmItem) {
      deleteItemMutation.mutate({ itemId: deleteConfirmItem });
      setDeleteConfirmItem(null);
    }
  }, [deleteConfirmItem, deleteItemMutation]);

  const handleToggleItem = useCallback(
    (itemId: number, currentStatus: boolean) => {
      toggleItemMutation.mutate({ itemId, isActive: !currentStatus });
    },
    [toggleItemMutation]
  );

  if (versionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!versionsQuery.data || versionsQuery.data.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium mb-2">Sem versões criadas</h3>
          <p className="text-muted-foreground mb-4">
            Crie a primeira versão da biblioteca de sugestões
          </p>
          <Button onClick={handleCreateVersion} disabled={createVersionMutation.isPending}>
            <Plus className="w-4 h-4 mr-2" />
            Criar DRAFT
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SEÇÃO A: Header de Governança */}
      <div className="space-y-4 border-b pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Matriz de Sugestões</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Biblioteca de recomendações para riscos psicossociais
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleCreateVersion}
              disabled={createVersionMutation.isPending}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar DRAFT
            </Button>
            {selectedVersion && (
              <>
                {isDraft && (
                  <>
                    <Button
                      onClick={() => setShowPublishConfirm(true)}
                      disabled={publishVersionMutation.isPending}
                    >
                      Publicar
                    </Button>
                    <Button
                      onClick={() => setShowCloneDialog(true)}
                      variant="outline"
                      disabled={cloneVersionMutation.isPending}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Clonar
                    </Button>
                  </>
                )}
                {isPublished && (
                  <>
                    <Button
                      onClick={() => setShowCloneDialog(true)}
                      variant="outline"
                      disabled={cloneVersionMutation.isPending}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Clonar
                    </Button>
                    <Button
                      onClick={() => setShowArchiveConfirm(true)}
                      variant="outline"
                      disabled={archiveVersionMutation.isPending}
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Arquivar
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => setIsAuditModalOpen(true)}
                  variant="ghost"
                  size="sm"
                >
                  <History className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Selector de Versão e Metadados */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-sm font-medium mb-2 block">Versão</label>
            <Select
              value={selectedVersionId?.toString() || ""}
              onValueChange={(value) => setSelectedVersionId(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma versão" />
              </SelectTrigger>
              <SelectContent>
                {versionsQuery.data?.map((version) => (
                  <SelectItem key={version.id} value={version.id.toString()}>
                    <div className="flex items-center gap-2">
                      {version.name}
                      <Badge
                        variant={
                          version.status === "PUBLISHED"
                            ? "default"
                            : version.status === "DRAFT"
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {version.status}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedVersion && (
            <>
              <div className="text-sm text-muted-foreground">
                <p className="font-medium">Criado em</p>
                <p>{new Date(selectedVersion.createdAt).toLocaleDateString("pt-BR")}</p>
              </div>
              {selectedVersion.publishedAt && (
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium">Publicado em</p>
                  <p>{new Date(selectedVersion.publishedAt).toLocaleDateString("pt-BR")}</p>
                </div>
              )}
            </>
          )}
        </div>

        {isPublished && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Esta versão está publicada e imutável. Clone para criar uma nova versão editável.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* SEÇÃO B: Filtros e Busca */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Buscar</label>
          <Input
            placeholder="Buscar por título ou descrição..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Tipo</label>
          <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="PREVENTION">Prevenção (4–8)</SelectItem>
              <SelectItem value="MITIGATION_ELIMINATION">Mitigação/Eliminação (&gt;8)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="ACTIVE">Ativo</SelectItem>
              <SelectItem value="INACTIVE">Inativo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isDraft && (
          <div className="flex items-end">
            <Button
              onClick={handleCreateItem}
              className="w-full"
              disabled={isPublished || isArchived}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Item
            </Button>
          </div>
        )}
      </div>

      {/* SEÇÃO C: Tabela de Sugestões */}
      <div className="border rounded-lg overflow-hidden">
        {itemsQuery.isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center p-8 text-muted-foreground">
            {isDraft ? "Nenhum item. Clique em 'Adicionar Item' para começar." : "Nenhum item encontrado"}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título/Resumo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Última atualização</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.title || "Sem título"}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-xs">
                        {item.recommendationText.substring(0, 120)}...
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {item.recommendationType === "PREVENTION"
                        ? "Prevenção"
                        : "Mitigação"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={item.isActive ? "default" : "secondary"}
                    >
                      {item.isActive ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(item.updatedAt).toLocaleDateString("pt-BR")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {isDraft && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              handleToggleItem(item.id, item.isActive)
                            }
                            disabled={toggleItemMutation.isPending}
                          >
                            {item.isActive ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditItem(item)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteItem(item.id)}
                            disabled={deleteItemMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {!isDraft && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditItem(item)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* SEÇÃO D: Editor Drawer */}
      <Drawer open={isEditDrawerOpen} onOpenChange={setIsEditDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {editingItem?.id ? "Editar Item" : "Novo Item"}
            </DrawerTitle>
            <DrawerDescription>
              {editingItem?.id
                ? "Atualize as informações do item de sugestão"
                : "Crie um novo item de sugestão"}
            </DrawerDescription>
          </DrawerHeader>

          {editingItem && (
            <div className="space-y-4 p-4 max-w-2xl mx-auto w-full">
              {/* Campo Módulo ERGOS */}
              <div>
                <label className="text-sm font-medium">Módulo ERGOS (Fonte Geradora) *</label>
                {editingItem.id ? (
                  // EDIT: Módulo read-only
                  <div className="flex items-center gap-2">
                    <Input
                      value={getModuleByKey(editingModuleKey)?.label || "Não vinculado"}
                      disabled
                      className="bg-muted"
                    />
                    <span className="text-xs text-muted-foreground" title="O módulo é fixo após criação">
                      🔒 Fixo
                    </span>
                  </div>
                ) : (
                  // CREATE: Módulo obrigatório
                  <Select
                    value={editingModuleKey}
                    onValueChange={(value) => {
                      setEditingModuleKey(value);
                      setEditingItem({ ...editingItem, moduleKey: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um módulo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ERGOS_MODULES.map((module) => (
                        <SelectItem key={module.key} value={module.key}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {!editingItem.id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    O módulo não poderá ser alterado após a criação.
                  </p>
                )}
              </div>

              {/* Título (Auto-gerado, read-only) */}
              <div>
                <label className="text-sm font-medium">Título (Auto-gerado)</label>
                <Input
                  value={
                    editingModuleKey && editingItem.recommendationType
                      ? `${getModuleByKey(editingModuleKey)?.label || "Módulo"} - ${
                          editingItem.recommendationType === "PREVENTION"
                            ? "Prevenção"
                            : "Mitigação/Eliminação"
                        }`
                      : "Selecione um módulo e tipo"
                  }
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  O título é gerado automaticamente com base no módulo e tipo.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Descrição *</label>
                <textarea
                  className="w-full p-2 border rounded-md"
                  rows={4}
                  value={editingItem.recommendationText || ""}
                  onChange={(e) =>
                    setEditingItem({
                      ...editingItem,
                      recommendationText: e.target.value,
                    })
                  }
                  placeholder="Descrição da recomendação"
                />
              </div>

              {/* Toggle Ativo/Inativo (apenas no EDIT) */}
              {editingItem.id && (
                <div className="flex items-center gap-3 p-3 border rounded-md">
                  <label className="text-sm font-medium flex-1">Status</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {editingItem.isActive ? "Ativo" : "Inativo"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newStatus = !editingItem.isActive;
                        setEditingItem({ ...editingItem, isActive: newStatus });
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editingItem.isActive ? "bg-green-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editingItem.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground col-span-2">
                    Apenas itens ativos são usados pelo motor de análise.
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Tipo *</label>
                <Select
                  value={editingItem.recommendationType || "PREVENTION"}
                  onValueChange={(value: any) =>
                    setEditingItem({
                      ...editingItem,
                      recommendationType: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREVENTION">Prevenção (4–8)</SelectItem>
                    <SelectItem value="MITIGATION_ELIMINATION">
                      Mitigação/Eliminação (&gt;8)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDrawerOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveItem}
                  disabled={
                    createItemMutation.isPending || updateItemMutation.isPending
                  }
                >
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      {/* Confirmação de Publicação */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publicar Versão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja publicar esta versão? Qualquer versão PUBLISHED anterior será arquivada.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setShowPublishConfirm(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishVersionMutation.isPending}
            >
              {publishVersionMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publicando...
                </>
              ) : (
                "Publicar"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Arquivamento */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arquivar Versão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja arquivar esta versão? Ela não será mais usada pelo motor de análise.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setShowArchiveConfirm(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleArchive}
              disabled={archiveVersionMutation.isPending}
              variant="destructive"
            >
              {archiveVersionMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Arquivando...
                </>
              ) : (
                "Arquivar"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Clone */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clonar Versão</DialogTitle>
            <DialogDescription>
              Crie uma cópia editável desta versão
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nome da nova versão</label>
              <Input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="ex: Biblioteca v2"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCloneDialog(false);
                  setCloneName("");
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleClone}
                disabled={cloneVersionMutation.isPending || !cloneName.trim()}
              >
                {cloneVersionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Clonando...
                  </>
                ) : (
                  "Clonar"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Exclusão */}
      <Dialog open={deleteConfirmItem !== null} onOpenChange={(open) => !open && setDeleteConfirmItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir sugestão?</DialogTitle>
            <DialogDescription>
              Essa ação não pode ser desfeita. Deseja excluir este item?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmItem(null)}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDeleteItem}
              disabled={deleteItemMutation.isPending}
              variant="destructive"
            >
              {deleteItemMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Auditoria */}
      <Dialog open={isAuditModalOpen} onOpenChange={setIsAuditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Histórico de Auditoria</DialogTitle>
            <DialogDescription>
              Histórico de ações realizadas nesta versão
            </DialogDescription>
          </DialogHeader>
          {auditLogQuery.isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : auditLogQuery.data && auditLogQuery.data.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {auditLogQuery.data.map((log) => (
                <div key={log.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{log.action}</span>
                    <span className="text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  {log.diffJson && (
                    <p className="text-muted-foreground mt-1">
                      {log.diffJson}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">Sem eventos de auditoria</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
